import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TrendingUp, Calendar } from 'lucide-react';
import type { LoanData } from './types';
import { formatCurrency, calculateAutonomousLoanDetails } from './types';
import { calcDefaultInstallmentDates } from '../../lib/workdays';

interface Props {
  data: LoanData;
  onComplete: (data: LoanData) => void;
}

export default function AutonomousSimulationStep({ data, onComplete }: Props) {
  const [amount, setAmount] = useState(data.amount || '');
  const [installments, setInstallments] = useState(data.installments || '');
  const [interestRate, setInterestRate] = useState(data.interestRate || '10');
  const [installmentDates, setInstallmentDates] = useState<Date[]>(data.installmentDates || []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const parsedAmount = parseFloat(amount) || 0;
  const parsedInstallments = parseInt(installments) || 0;

  useEffect(() => {
    if (parsedInstallments >= 1 && parsedInstallments <= 100) {
      setInstallmentDates(calcDefaultInstallmentDates(parsedInstallments, true));
    } else {
      setInstallmentDates([]);
    }
  }, [parsedInstallments]);

  const details = parsedAmount > 0 && parsedInstallments > 0
    ? calculateAutonomousLoanDetails(amount, installments, interestRate)
    : null;

  const handleDateChange = (date: Date | null, index: number) => {
    if (!date) return;
    setInstallmentDates(prev => prev.map((d, i) => (i === index ? date : d)));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0)
      errs.amount = 'Informe o valor do empréstimo';
    if (!installments || parsedInstallments < 1)
      errs.installments = 'Informe o número de parcelas';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleContinue = () => {
    if (!validate()) return;
    onComplete({
      ...data,
      amount,
      installments: parsedInstallments.toString(),
      interestRate,
      isCustom: true,
      installmentDates,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Simulação — Autônomo</h2>
        <p className="text-gray-600 text-sm">Configure os valores financeiros do contrato.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Valor do Empréstimo (R$) *
        </label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          min="0"
          step="0.01"
          placeholder="0,00"
          className={`w-full px-4 py-3 border rounded-xl text-base transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.amount ? 'border-red-400 bg-red-50' : 'border-gray-300'
          }`}
        />
        {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Número de Parcelas *
        </label>
        <input
          type="number"
          value={installments}
          onChange={(e) => setInstallments(e.target.value)}
          min="1"
          max="100"
          placeholder="Ex: 3"
          className={`w-full px-4 py-3 border rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.installments ? 'border-red-400 bg-red-50' : 'border-gray-300'
          }`}
        />
        {errors.installments && <p className="text-red-500 text-xs mt-1">{errors.installments}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Taxa de Juros *
        </label>
        <div className="relative">
          <input
            type="number"
            value={interestRate}
            onChange={(e) => setInterestRate(e.target.value)}
            min="1"
            max="1000"
            step="0.1"
            placeholder="Ex: 10"
            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none">%</span>
        </div>
      </div>

      {installmentDates.length > 0 && (
        <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
            <Calendar className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700">Datas das Parcelas</h3>
            <span className="ml-auto text-xs text-gray-400">Ajuste conforme necessário</span>
          </div>
          <div className="p-4 space-y-3">
            {installmentDates.map((date, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="flex-shrink-0 w-24 text-sm font-medium text-gray-700">
                  Parcela {index + 1}
                </span>
                <DatePicker
                  selected={date}
                  onChange={(d) => handleDateChange(d, index)}
                  dateFormat="dd/MM/yyyy"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:block w-28">
                  {date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {details && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-gray-800">Resumo da Simulação</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Valor solicitado</span>
              <span>{formatCurrency(parsedAmount)}</span>
            </div>
            <div className="flex justify-between font-semibold text-emerald-700">
              <span>Valor que o cliente receberá</span>
              <span>{formatCurrency(details.amountReceived)}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 space-y-1.5">
              <div className="flex justify-between text-gray-600">
                <span>Valor/parcela sem juros</span>
                <span>{formatCurrency(details.baseInstallmentAmount)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Taxa de juros</span>
                <span>{interestRate}%</span>
              </div>
              <div className="flex justify-between text-gray-700 font-medium">
                <span>Valor/parcela com juros</span>
                <span>{formatCurrency(details.installmentAmount)}</span>
              </div>
            </div>
            <div className="flex justify-between font-bold text-gray-900 text-base border-t border-gray-200 pt-2">
              <span>Total a pagar</span>
              <span>{formatCurrency(details.totalAmount)}</span>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleContinue}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-base hover:bg-blue-700 transition-colors shadow-sm"
      >
        Continuar
      </button>
    </div>
  );
}
